/**
 * Recherche Ki Para — LA règle unique de recherche par lettres (côté app).
 *
 * Même règle que `BackEnd/django_pos/search.py` : toute liste filtrée en
 * tapant du texte (clients, produits, fournisseurs, factures, soins, comptes…)
 * passe par ici, pour que « taper des lettres » se comporte partout pareil.
 *
 * La règle
 * 1. Insensible à la casse ET aux accents (« avene » trouve « Avène »).
 * 2. Chaque mot tapé doit être le DÉBUT d'un mot du texte cherché.
 *    « h » → Hamza, Ben Hassen… mais PAS Ahlem. « ben ha » → Ben Hassen.
 * 3. Les codes (téléphone, code-barres, référence, n° de facture, e-mail) se
 *    cherchent « contient » : on tape souvent le milieu d'un numéro.
 * 4. Ordre : ce qui COMMENCE par la phrase tapée d'abord, puis les mots qui
 *    commencent dedans ; à l'intérieur d'un rang l'ordre de la liste est gardé.
 * 5. On cherche dès la 1re lettre.
 */

/** Minuscules sans accents, espaces réduits — la forme « tapée au clavier ». */
export const fold = (x) =>
  (x == null ? '' : String(x))
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** Les mots tapés, normalisés (vides retirés). */
export const searchTerms = (query) => fold(query).split(' ').filter(Boolean);

const WORD_SPLIT = /[^a-z0-9]+/;

const wordStarts = (foldedText, term) => {
  if (!foldedText) return false;
  if (foldedText.startsWith(term)) return true;
  return foldedText.split(WORD_SPLIT).some((w) => w && w.startsWith(term));
};

/**
 * Le texte « mots » (ou tableau de textes) + les codes correspondent-ils à la
 * phrase tapée ? Tous les mots tapés doivent matcher (sur un texte ou un code).
 */
export function matchesSearch(query, text, codes = []) {
  const terms = searchTerms(query);
  if (!terms.length) return true;
  const texts = (Array.isArray(text) ? text : [text]).map(fold).filter(Boolean);
  const codeList = (Array.isArray(codes) ? codes : [codes]).map(fold).filter(Boolean);
  return terms.every(
    (t) => texts.some((x) => wordStarts(x, t)) || codeList.some((c) => c.includes(t)),
  );
}

/** 0 si un des textes COMMENCE par la phrase tapée, sinon 1. */
export function searchRank(query, text) {
  const phrase = searchTerms(query).join(' ');
  if (!phrase) return 0;
  const texts = (Array.isArray(text) ? text : [text]).map(fold);
  return texts.some((x) => x.startsWith(phrase)) ? 0 : 1;
}

/**
 * Filtre + classe une liste selon la règle.
 *   filterSearch(clients, q, { text: (c) => [c.first_name, c.last_name], codes: (c) => [c.phone] })
 * `text`  : fonction → texte ou tableau de textes « mots » de l'élément.
 * `codes` : fonction → code(s) cherchés « contient » (optionnel).
 * Phrase vide → la liste telle quelle.
 */
export function filterSearch(items, query, { text, codes } = {}) {
  const list = Array.isArray(items) ? items : [];
  if (!searchTerms(query).length) return [...list];
  const getText = text || ((x) => x?.name);
  const getCodes = codes || (() => []);
  return list
    .map((item, i) => ({ item, i, rank: searchRank(query, getText(item)) }))
    .filter(({ item }) => matchesSearch(query, getText(item), getCodes(item)))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map(({ item }) => item);
}
